/*
 * <BeTooltip> -- markup for Be's TipWindow bubble-help (Notes/24). components/
 * tooltip.scss does the work: the dwell, the fade, the anchored placement and its
 * flip. This wires `aria-describedby` to the bubble and turns props into the
 * classes and custom properties that sheet reads.
 */
import { type CSSProperties, type ReactNode, useId, useImperativeHandle, useRef } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeTooltipPlacement = "below" | "above" | "auto";

export type BeTooltipProps = BeProps<
	"span",
	{
		/** The bubble's content (string or rich nodes). */
		tip: ReactNode;
		/** The trigger element the tip is attached to. */
		children: ReactNode;
		/** ms of hover/focus dwell before showing. Default 1000 (source: 1500). */
		delay?: number;
		/** Below, above, or auto (flip above when it would clip the viewport). Default "auto". */
		place?: BeTooltipPlacement;
		/** Inline/popover: stay open when the cursor moves onto the bubble. */
		sticky?: boolean;
		/** Override the bubble fill (--be-tooltip-bg). */
		fillColor?: string;
		/** Override the text colour (--be-tooltip-text). */
		textColor?: string;
	}
>;

export const BeTooltip = beComponent<BeTooltipProps, HTMLSpanElement>("BeTooltip", (props, be) => {
	const { tip, children, delay, place = "auto", sticky = false, fillColor, textColor, scale, className, style, ...rest } = props;

	const tipId = useId();
	// forwarded ref mirrors the internal one, which the imperative handle exposes
	const anchorRef = useRef<HTMLSpanElement>(null);

	const tipStyle: CSSProperties = {};
	if (fillColor) tipStyle["--be-tooltip-bg"] = fillColor;
	if (textColor) tipStyle["--be-tooltip-text"] = textColor;
	if (delay !== undefined) tipStyle["--be-tip-delay"] = `${delay}ms`;

	const { ref: forwarded, ...anchor } = be("be-tip-anchor");
	useImperativeHandle(forwarded, () => anchorRef.current as never, []);
	const tipClass = sticky ? "be-tooltip be-tooltip--inline" : "be-tooltip";

	return (
		<span aria-describedby={tipId} {...rest} {...anchor} ref={anchorRef}>
			{children}
			<span id={tipId} className={tipClass} role="tooltip" style={tipStyle} data-be-place={place === "auto" ? undefined : place}>
				{/* the bubble paints its own face and bevel, and .be-pixel-text must
				    not sit on a background-painting element */}
				{pixelText(tip)}
			</span>
		</span>
	);
});
